// ── Dictionary entry (matches dictionary_entries table) ───────────────────────

export type EntryStatus = 'verified' | 'unverified' | 'needs_review'

export type SuggestionType =
  | 'better_magar'
  | 'better_nepali'
  | 'better_english'
  | 'dialect_variant'
  | 'wrong_entry'
  | 'other'

export type ReviewStatus = 'pending' | 'accepted' | 'rejected' | 'merged'

export interface DictionaryEntry {
  id: number
  entry_id: string
  magar_form_devanagari: string
  magar_form_romanized: string
  magar_variant_note: string
  pos: string
  english_gloss: string
  nepali_form_romanized: string
  nepali_form_devanagari: string
  category: string
  dialect: string
  notes: string
  source: string
  frequency: number
  status: EntryStatus
  status_rank: number
}

// Lightweight version returned by search RPC
export type EntrySearchRow = Pick<
  DictionaryEntry,
  | 'id'
  | 'entry_id'
  | 'magar_form_devanagari'
  | 'magar_form_romanized'
  | 'magar_variant_note'
  | 'pos'
  | 'english_gloss'
  | 'nepali_form_romanized'
  | 'nepali_form_devanagari'
  | 'category'
  | 'dialect'
  | 'source'
  | 'status'
  | 'status_rank'
> & { total_count: number }

// ── Suggestion ─────────────────────────────────────────────────────────────

export interface Suggestion {
  id: string
  entry_id: string
  submitter_name: string | null
  submitter_email: string | null
  suggestion_type: SuggestionType
  corrected_magar: string | null
  corrected_nepali: string | null
  corrected_english: string | null
  dialect_note: string | null
  speaker_notes: string | null
  submitted_at: string
  review_status: ReviewStatus
  reviewed_by: string | null
  reviewed_at: string | null
  admin_notes: string | null
}

export interface SuggestionWithEntry extends Suggestion {
  entry: DictionaryEntry
}

// ── New suggestion form payload ─────────────────────────────────────────────

export interface SuggestionPayload {
  entry_id: string
  submitter_name: string
  submitter_email: string
  suggestion_type: SuggestionType
  corrected_magar: string
  corrected_nepali: string
  corrected_english: string
  dialect_note: string
  speaker_notes: string
}

// ── Supabase database type shim ─────────────────────────────────────────────

export type Database = {
  public: {
    Tables: {
      dictionary_entries: {
        Row: DictionaryEntry
        Insert: Omit<DictionaryEntry, 'id' | 'status_rank'>
        Update: Partial<Omit<DictionaryEntry, 'id' | 'status_rank'>>
      }
      suggestions: {
        Row: Suggestion
        Insert: Omit<Suggestion, 'id' | 'submitted_at' | 'review_status' | 'reviewed_by' | 'reviewed_at' | 'admin_notes'>
        Update: Partial<Suggestion>
      }
    }
    Functions: {
      search_entries: {
        Args: { search_term: string; page_offset?: number; page_limit?: number }
        Returns: EntrySearchRow[]
      }
    }
  }
}

// ── Display helpers ─────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<EntryStatus, string> = {
  verified:     'Verified',
  unverified:   'Unverified',
  needs_review: 'Needs Review',
}

export const SUGGESTION_TYPE_LABELS: Record<SuggestionType, string> = {
  better_magar:    'Better Magar form',
  better_nepali:   'Better Nepali form',
  better_english:  'Better English gloss',
  dialect_variant: 'Dialect variant / alternate form',
  wrong_entry:     'This entry is wrong or a duplicate',
  other:           'Other note / comment',
}
